import { useTranslation } from "react-i18next"

import type { Document } from "@/api/models"

import { ArrowUpRight, Calendar, FileText, Mail, MessageSquare } from "@/components/icons"
import { cn } from "@/lib/utils"

import { buttonVariants } from "../../../components/ui/button-variants"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip"
import { resolveM365CitationDisplay } from "../utils/m365-citation-display"

type M365CitationItemProps = {
  citation: string
  index: number
  documents: [string, Document][]
  onNavigateToInline: (index: number) => void
  isHighlighted?: boolean
  messageIndex?: number
}

export function M365CitationItem({
  citation,
  index,
  documents,
  onNavigateToInline,
  isHighlighted = false,
  messageIndex,
}: M365CitationItemProps) {
  const { t } = useTranslation()
  const { metadata, title, type: m365Type, url } = resolveM365CitationDisplay(citation, documents)

  function TypeIcon() {
    switch (m365Type) {
      case "email":
        return <Mail className="text-muted-foreground size-3" />
      case "file":
        return <FileText className="text-muted-foreground size-3" />
      case "event":
        return <Calendar className="text-muted-foreground size-3" />
      case "teams":
        return <MessageSquare className="text-muted-foreground size-3" />
      default:
        return <FileText className="text-muted-foreground size-3" />
    }
  }

  const content = (
    <div
      data-citation-index={index}
      data-message-index={messageIndex}
      role="presentation"
      className={cn(
        "border-border/50 bg-muted/30 hover:border-primary/30 flex items-center gap-2 rounded-md border p-2 text-sm transition-colors",
        isHighlighted && "animate-[highlight_2s_ease-in-out]",
        "cursor-pointer"
      )}
      // Mouse convenience for the whole row; keyboard users activate the inner button,
      // whose click bubbles up to this handler.
      onClick={() => {
        onNavigateToInline(index)
      }}
    >
      <button
        className="focus-visible:ring-ring flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-sm text-left focus-visible:ring-1 focus-visible:outline-none"
        type="button"
      >
        <span className="text-muted-foreground ml-1 shrink-0 text-xs font-medium">
          {index + 1}.
        </span>
        <span className="bg-muted flex size-5 shrink-0 items-center justify-center rounded-full">
          <TypeIcon />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-foreground line-clamp-1 text-xs font-medium" title={title}>
            {title}
          </span>
          {metadata && (
            <span className="text-muted-foreground line-clamp-1 text-xs" title={metadata}>
              {metadata}
            </span>
          )}
        </span>
      </button>
      <div className="flex shrink-0 items-center gap-1">
        <span className="bg-muted-foreground/10 text-muted-foreground rounded px-1.5 py-0.5 text-[10px] font-medium">
          {m365Type ? t(`answer.m365_${m365Type}`) : t("answer.m365_sources")}
        </span>
        {url && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={url}
                rel="noopener noreferrer"
                target="_blank"
                className={cn(
                  buttonVariants({
                    size: "icon",
                    variant: "ghost",
                  }),
                  "h-6 w-6 p-0"
                )}
                onClick={(event) => {
                  event.stopPropagation()
                }}
              >
                <ArrowUpRight className="size-4" />
              </a>
            </TooltipTrigger>
            <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
              <p>{t("answer.show_document")}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
  return content
}
